import React, { useState } from 'react';
import { IonItem, IonLabel, IonIcon } from '@ionic/react';
import { chevronDownOutline, chevronUpOutline } from 'ionicons/icons';
import type { TopicMutation } from './mutations';
import { formatMutationTimestamp } from './mutations';
import './TopicChangeItem.css';

interface TopicChangeItemProps {
  mutation: TopicMutation;
  /**
   * Rendered at the end of the header row, before the expand chevron — e.g.
   * the Changes page's per-row Share Updates button (#427). Interactive
   * content must stopPropagation so it doesn't toggle the row.
   */
  endSlot?: React.ReactNode;
}

const renderValue = (value: string, className: string) =>
  value.trim() === '' ? (
    <span className={`${className} topic-change-item__empty`}>(empty)</span>
  ) : (
    <span className={className}>{value}</span>
  );

/**
 * A single collapsible entry in the topic change-history list: the header row
 * shows the changed field and when it changed; expanding reveals that field's
 * before → after diff (#418).
 */
const TopicChangeItem: React.FC<TopicChangeItemProps> = ({
  mutation,
  endSlot,
}) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <IonItem
        button
        detail={false}
        onClick={() => setOpen(prev => !prev)}
        aria-expanded={open}
        data-testid="topic-change-item"
        data-field={mutation.field}
      >
        <IonLabel>
          <h2 style={{ fontWeight: 600 }}>{mutation.label}</h2>
          <p className="topic-change-item__timestamp">
            {formatMutationTimestamp(mutation.at)}
          </p>
        </IonLabel>
        {endSlot}
        <IonIcon
          icon={open ? chevronUpOutline : chevronDownOutline}
          slot="end"
          aria-hidden="true"
        />
      </IonItem>
      {open && (
        <div
          className="topic-change-item__body"
          data-testid="topic-change-item-body"
        >
          {renderValue(mutation.before, 'topic-change-item__before')}
          {renderValue(mutation.after, 'topic-change-item__after')}
        </div>
      )}
    </>
  );
};

export default TopicChangeItem;
